import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import LoginPopup from "./LoginPopup";
import axios from "axios";

const OfertaCard = ({ oferta, user, isFavorite, onFavoriteToggle, likes = 0 }) => {
  const { currentUser, getFreshToken } = useAuth();
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // Helper to safely format currency
  const formatPrice = (value) => {
    if (value === undefined || value === null) return "R$ --";
    return `R$ ${Number(value).toFixed(2)}`;
  };

  // Helper to calculate discount safely
  const getDiscountPercentage = () => {
    if (!oferta.precoOriginal || !oferta.precoDesconto) return 0;
    return Math.round(
      ((oferta.precoOriginal - oferta.precoDesconto) / oferta.precoOriginal) * 100
    );
  };

  const handleOfferClick = async (e) => {
    if (!currentUser) {
      e.preventDefault();
      // Salva a URL do produto no sessionStorage para redirect após o login
      sessionStorage.setItem('pendingLoginRedirect', `/oferta/${oferta.id}`);
      setShowLoginPopup(true);
    } else {
      // Tentar pontuar pelo clique e registrar visualização
      try {
        const token = await getFreshToken();
        
        // Registrar visualização e interesse (paralelo)
        Promise.all([
          axios.post(`${process.env.REACT_APP_API_URL}/wallet/click-reward`,
            { offerId: oferta.id },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.post(`${process.env.REACT_APP_API_URL}/offers/${oferta.id}/view`),
          // Rastrear interesse por categoria localmente
          (async () => {
            if (oferta.categoria) {
              const interests = JSON.parse(localStorage.getItem('categoryInterests') || '{}');
              interests[oferta.categoria] = (interests[oferta.categoria] || 0) + 1;
              localStorage.setItem('categoryInterests', JSON.stringify(interests));
            }
          })()
        ]).catch(console.error);
        
      } catch (error) {
        console.error("Erro ao registrar interações:", error);
      }
    }
  };

  const handleLoginSuccess = React.useCallback(() => {
    setShowLoginPopup(false);
    // O redirect é tratado pelo App.js que monitora a mudança do user
    // via sessionStorage('pendingLoginRedirect')
  }, []);

  const discountPercentage = getDiscountPercentage();
  const precoOriginal = oferta.precoOriginal;
  const precoDesconto = oferta.precoDesconto;

  return (
    <div style={{
      backgroundColor: 'var(--white)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-sm)',
      overflow: 'hidden',
      transition: 'transform 0.2s, box-shadow 0.2s',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      {/* Favorite Button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          onFavoriteToggle(oferta.id);
        }}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'rgba(255, 255, 255, 0.9)',
          border: 'none',
          borderRadius: '50%',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 2,
          transition: 'all 0.2s',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.background = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
        }}
      >
        <i className={isFavorite ? "fas fa-heart" : "far fa-heart"} style={{
          color: isFavorite ? 'var(--accent-color)' : 'var(--text-secondary)',
          fontSize: '16px'
        }}></i>
      </button>

      <Link
        to={`/oferta/${oferta.id}`}
        onClick={handleOfferClick}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        {/* Image Container */}
        <div style={{
          width: '100%',
          paddingTop: '100%',
          position: 'relative',
          backgroundColor: 'var(--background-light)',
          overflow: 'hidden'
        }}>
          <img
            src={oferta.imagem}
            alt={oferta.titulo}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: 'var(--spacing-sm)'
            }}
          />

          {/* Like Badge */}
          <div style={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '4px 8px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            fontWeight: 'bold',
            color: 'var(--text-secondary)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1
          }}>
            <i className="fas fa-thumbs-up" style={{ color: 'var(--primary-color)' }}></i>
            {likes}
          </div>
        </div>

        {/* Content */}
        <div style={{
          padding: 'var(--spacing-md)',
          display: 'flex',
          flexDirection: 'column',
          flex: 1
        }}>
          <div style={{ marginBottom: 'var(--spacing-xs)' }}>
            <span style={{
              fontSize: '10px',
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              letterSpacing: '0.5px'
            }}>
              {oferta.loja}
            </span>
          </div>

          <h3 style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--text-primary)',
            margin: '0 0 var(--spacing-sm) 0',
            lineHeight: '1.4',
            fontWeight: 'normal',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            height: '44px'
          }}>
            {oferta.titulo}
          </h3>

          <div style={{ marginTop: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ textDecoration: 'line-through', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                {formatPrice(precoOriginal)}
              </span>
              {discountPercentage > 0 && (
                <span style={{
                  backgroundColor: 'var(--accent-color)',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}>
                  {discountPercentage}% OFF
                </span>
              )}
            </div>

            <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatPrice(precoDesconto)}
            </div>

            {oferta.mostrar_parcelamento !== false && precoDesconto && (
              <div style={{ fontSize: '12px', color: 'var(--accent-color)', fontWeight: 'bold', marginTop: '4px' }}>
                {oferta.parcelas || 10}x R$ {(Number(precoDesconto) / (oferta.parcelas || 10)).toFixed(2)} sem juros
              </div>
            )}
          </div>
        </div>
      </Link>

      {/* Login Popup */}
      {showLoginPopup && (
        <LoginPopup
          onClose={() => setShowLoginPopup(false)}
          onSuccess={handleLoginSuccess}
        />
      )}
    </div>
  );
};

export default OfertaCard;
